import {ActivityWindow, DomWindow, DomWindowConfig} from './DomWindow';
import { MenuSource, MessageBarAction, ProgressBar, SearchableMenu} from './DomMenu';
import {Memo, MemoItem} from './Memo';
import { Markdown } from './util/Markdown';
import { LogWindow, ChatWindow, UserMgmtWindow, FileListWindow, ItemSearchWindow, CalendarWindow } from './AppWindows';
import $ from 'jquery';
import {BrowserCrypto, PinboardAppNativeCrypto, WebSocketRemote} from './Connection';
import { PluginBase } from './Plugin';
import { arraySignal, observable, signal } from './lib/signal';
import { IEditorWindow } from './IEditorWindow';
import { DockLayoutPlugin } from './plugins/DockLayout';
import { LocalDb } from './db/Database';
import { MemoItemSearchMatcher } from './util/AppUtils';
import { UiContext } from './util/UiContext';
import { SelectionManager } from './memo/MemoSelectionManager';
import { PinboardObjectApi } from './PinboardObjectApi';
import {dragdrop1d, EL} from "./util/DomUtils";
import {BoundMethod, shallowClone, sum} from "./util/CoreUtils";
import {DEFAULT_MEMO_ACTIONS} from "./memo/DefaultActions";
import {slot} from "./lib/slot";
import {Expression} from "./util/expr/Expr";
import { Command} from "./ActionManager";
import {MruList} from "./util/MruList";
import {MountInfo} from "./IMemoRemote";
import {MemoActivity} from "./memo/MemoActivity";
import { NavigationOptions} from "./ui/Activity";
import {InMemoryDocument} from "./db/WritableDocument";
import {ActivityHolder, registerActivityResolvers} from "./ui/ActivityHolder";
import {showLegacySyncDialog} from "./db/LegacySyncDialog";

declare var __BUNDLE_INFO__: { version: string, buildTimestamp: number, environment: string };

export class App extends UiContext {

    @observable
    public accessor remote : WebSocketRemote;
    @observable
    public accessor _db : LocalDb;
    public wndLog : LogWindow;
    public wndEdit : IEditorWindow;
    public selectionManager : SelectionManager<Memo, MemoItem>;

    public get myMemo() : MemoActivity|null {
        return this.mainViewer.activity instanceof MemoActivity ? this.mainViewer.activity : null;
    }
    @observable
    public accessor mainViewer: ActivityHolder;
    public wndParkplatz : ActivityWindow;
    public wndAutostart : ActivityWindow;
    public wndAutostartFull : ActivityWindow;
    public wndChat : ChatWindow;
    public wndLogin : UserMgmtWindow;
    public wndFileList : FileListWindow;
    public wndCalendar : CalendarWindow;
    @observable
    public memoListRequested = arraySignal<() => Memo[]>();
    public notificationClicked = signal<(notification: any) => void>();
    public get memos() : Memo[] {
        return this.memoListRequested.flatCollect();
    };
    public availablePlugins : (typeof PluginBase)[] = [
        DockLayoutPlugin
    ];
    public activePlugins : PluginBase[] = [];
    public readonly bundleInfo : {[key: string]: any} = __BUNDLE_INFO__;
    public serviceWorkerRegistration : ServiceWorkerRegistration | null = null;
    public lastPasteStringItems = {};
    public readonly crypto = PinboardAppNativeCrypto.isAvailable() ? new PinboardAppNativeCrypto() : new BrowserCrypto();

    constructor(db : LocalDb) {
        super(
            new Markdown(),
            db,
            new SelectionManager<Memo, MemoItem>(DEFAULT_MEMO_ACTIONS)
        );
        this._db = db;
        this.resolver.protocols['doc:'] = db;
        registerActivityResolvers(this.resolver);
        this.selectionManager = this.sel as SelectionManager<Memo, MemoItem>;
        const app = this;
        this.mainViewer = new ActivityHolder({
            get children() { return [app.mainViewer.activity] },
            parent: undefined,
            [Symbol.dispose](): void {
            },
            async navigate(url: URL, navigationOptions?: NavigationOptions): Promise<void> {
                const options = Object.assign({}, navigationOptions, {previousUrl: app.mainViewer.url});
                await app.mainViewer.onBeforeNavigate(url, options);
                await app.mainViewer.onNavigate(url, options);
            },
            close(): void {
            },
            setButtons(buttons: HTMLButtonElement[]): void {
            },
            setContent(content: HTMLElement): void {
                document.getElementById("myMemo")!!.replaceChildren(content);
            },
            setTitle(title: string): void {
                document.title = title;
            }
        });
        var isMobileMode = "ontouchstart" in document.body;
        if (localStorage.overrideMobileMode === "TRUE") isMobileMode = true;
        if (localStorage.overrideMobileMode === "FALSE") isMobileMode = false;
        document.body.classList.add("mobile-"+isMobileMode);
        DomWindowConfig.set({ isMobileMode: isMobileMode });

        this.db.backingRemote = this.remote = new WebSocketRemote(this.crypto, this.bundleInfo.defaultWebsocketTarget);
        
        if (localStorage.websocketTarget) {
            const wsBuffered = EL('span', {});
            const wsOutstanding = EL('span', {});
            const wsReadyState = EL('span', {});
            const websocketIndicator = EL('div', {
                style:'background:yellow;font-style:italic;overflow: hidden; text-overflow: ellipsis; white-space: nowrap;',
                insertAfter: document.getElementById('docId'),
                onclick: (e) => {

                },
                oncontextmenu: (e) => {
                    e.preventDefault();
                    if (!localStorage.websocketAltTarget) return;
                    if(!confirm("Are you sure? This clears all locally cached/unsynced data.")) return;
                    var alt = localStorage.websocketAltTarget;
                    localStorage.websocketAltTarget = localStorage.websocketTarget;
                    localStorage.websocketTarget = alt;
                    this.remote.websocketDisconnect();
                    this.db.clearLocalData();
                    location=location;
                }
            }, wsBuffered, wsOutstanding, wsReadyState, localStorage.websocketTarget);
            setInterval(() => {
                const info = this.remote.connectionStateInfo;
                wsBuffered.innerText = info.bufferedAmount ? `(${info.bufferedAmount})` : '';
                wsBuffered.innerText = info.outstanding ? `[${info.outstanding}]` : '';
                wsReadyState.innerText = {'undefined': '-', '0': '🔄' /*CONNECTING*/, '1': '✔️' /*OPEN*/, '2': '💔' /*CLOSING*/, '3': '❌'/*CLOSED*/ }[info.readyState]
                    + (info.delay > 1000 ? ` ⌛${info.delay/1000}` : '');
            }, 2000);
        }
    }

    log_(type: string, context: any, message: string, actions: MessageBarAction[] = [], ...params: any) {
        this.wndLog?.print(message, {className: 'log-'+type}, params.length === 0 ? undefined : params, context?.script ? (actions ?? []).concat([ [ '//', () => this.navigate(context.script.idPath) ] ]) : actions);
        super.log_(type, context, message, actions, ...params);
    }

    @slot('memoListRequested', ($) => $)
    onMemoListRequested() {
        return this.myMemo ? [ this.myMemo ] : [];
    }

    @slot('remote', ($) => $.connected)
    async onRemoteConnected(helloParams: any, context: any) {
        this.log(`Connected to ${this.remote.remoteUrl}`);

        if ("bundle_info" in helloParams) {
            if (this.bundleInfo.buildTimestamp < helloParams.bundle_info.buildTimestamp) {
                this.logError(`Outdated client version! (local = ${this.bundleInfo.version}, available = ${helloParams.bundle_info.version})`);
                if (this.serviceWorkerRegistration) {
                    this.serviceWorkerRegistration.update();
                }
            }
            this.log(`Local bundle version: ${this.bundleInfo.version}`);
            this.log(`Available bundle version: ${helloParams.bundle_info.version}`);
        }

        if ($("#um_password").val())
            this.wndLogin.login();
        else {
            this.wndFileList.loadFilelist();
        }
    }

    @slot('remote', ($) => $.event('pb', 'pb', 'chat_event'))
    async onChatEvent(ctx: any, chat: any) {
        if (this.wndChat) this.wndChat.print("["+chat.from+"] "+chat.message);
        if (!this.wndChat || this.wndChat.collapsed) UiContext.instance.toast('info', 'Chat message: '+" ["+chat.from+"] "+chat.message, 2500);
    }

    @slot('remote', ($) => $.event('pb', 'pb', 'eval_event'))
    async onEvalEvent(ctx: any, from: string, script: string) {
        console.log('eval_event', ctx.p, from, script);
        if (ctx.p === 'pb/')
            eval(script);
    }

    @slot('remote', ($) => $.event('pb', 'pb', 'server_message'))
    async onServerMessage(ctx: any, json: any) {
        if (this.wndLog) this.log("ℹ️ "+json.server_message);
        UiContext.instance.toast("success", json.server_message, 2000);
    }

    async initUser(username: string) {
        this.settingsDoc = username ? await this.db.getDocument(localStorage.loginUser + '/settings') : new InMemoryDocument();

        if (username) {
            this.wndParkplatz.navigate(new URL("memo:" + username + "/parkplatz"), { pinned: true });
            this.wndAutostart.navigate(new URL("memo:" + username + "/zz/scripts/autostart"), { pinned: true }).then(() => this.selectionManager.addDynamicToolbarSource(this.wndAutostart.activityHolder.activity as MemoActivity));
            this.wndAutostartFull?.navigate(new URL("memo:" + username + "/zz/scripts/autostart-full"), { pinned: true }).then(() => this.selectionManager.addDynamicToolbarSource(this.wndAutostartFull.activityHolder.activity as MemoActivity));
        } else {
            this.wndParkplatz.navigate(new URL('memo:' + this.db.newSessionDocument().documentId), { pinned: true });
            this.wndAutostart.navigate(new URL('memo:' + this.db.newSessionDocument().documentId), { pinned: true }).then(() => this.selectionManager.addDynamicToolbarSource(this.wndAutostart.activityHolder.activity as MemoActivity));
            this.wndAutostartFull?.navigate(new URL('memo:' + this.db.newSessionDocument().documentId), { pinned: true }).then(() => this.selectionManager.addDynamicToolbarSource(this.wndAutostartFull.activityHolder.activity as MemoActivity));
        }
    }

    async getFilelistRoots(): Promise<MountInfo[]> {
        const mounts: any[] = (await this.search('document=~/settings opt=mount'));
        if (!mounts.length && localStorage.loginUser) mounts.push({ user: localStorage.loginUser });
        return mounts.filter(m => typeof m.user === 'string');
    }

    @slot('mainViewer', ($: ActivityHolder) => $.beforeNavigate)
    onMyMemoBeforeNavigate(newDocId: URL, oldDocId: URL) {
        console.log("beforeNavigate",location.search, newDocId, oldDocId)
        if (this.myMemo)
            history.replaceState({
                scrollLeft: this.myMemo.scrollContainer.scrollLeft,
                scrollTop: this.myMemo.scrollContainer.scrollTop, }, '');
    }
    @slot('mainViewer', ($: ActivityHolder) => $.navigated)
    onMyMemoNavigated(docId: URL) {
        const path = docId.toString().split('/');
        document.getElementById("docId").replaceChildren(...path.map((x, i) => EL(i+1 === path.length ? 'strong' : 'small', {}, x)));
        document.title=docId.toString()+" - pinboard";
        console.log("navigated",location.search, docId)
        if (!location.search)
            history.replaceState(null, '', '?'+docId.toString());
        else if (location.search != '?'+docId.toString())
            history.pushState({  }, '', '?'+docId.toString());
        if (docId.protocol === 'memo:') this.storeMru('documentMru', docId.pathname, 15);
    }
    
    async _initGlobal() {
        await this.crypto.init();

        if (localStorage.enable_rpc_logging) this.remote.enableLogging();

        // Declare MRU Lists
        this.storeMru('documentMru', null, 15);
        this.storeMru('domWindowMru', null, 15, false, MruList.urlEqHash);
        this.storeMru('commandPaletteMru', null, 20);
        this.storeMru('documentBookmarksMru', null, 20);
    }

    _initAppViewer() {
        //this.myMemo = new Memo(document.getElementById("myMemo")!!, this.selectionManager, undefined, { readonly: true, interactive: false, alwaysAllowIconClick: true });
        //this.selectionManager.activeMemo = this.myMemo;
        this._initNavigationHandler();
        this.markdown.loadPlugins();
    }
    onLinkClicked(linkElement: HTMLAnchorElement, event: MouseEvent) {
        const link = linkElement.href;
        if (link.startsWith("javascript:")) return;
        event.preventDefault();
        for (const prefix of [location.href.replace(/\?.*/,"")+'?', 'meow:']) {
            if (link.startsWith(prefix)) {
                const docId = link.substring(prefix.length).split('#');
                const keepScrollPos = linkElement.hasAttribute('data-keep-scroll-pos') ? linkElement.getAttribute('data-keep-scroll-pos') === 'true' : null;
                this.navigate(docId[0], docId[1], event.altKey || linkElement.getAttribute('target') === '_blank', keepScrollPos);
                return false;
            }
        }
        if ('__TAURI__' in window) {
            (async function() {
                if (!link.startsWith('http://') && !link.startsWith('https://')) {
                    if (!await confirm('Really open the following external link?\n\n'+link)) return;
                }
                (window as any).__TAURI__.shell.open(link);
            })();
        } else if ('nw' in window) {
            (async function() {
                if (!link.startsWith('http://') && !link.startsWith('https://')) {
                    if (!await confirm('Really open the following external link?\n\n'+link)) return;
                }
                (window as any).nw.Shell.openExternal(link);
            })();
        } else {
            window.open(link);
        }
        return false;
    }
    private _initNavigationHandler() {
        function findLink(e: Event) {
            if (e.composed && e.composedPath) return e.composedPath().find((el: any) => el.tagName === 'A' && el.hasAttribute('href'));
            // else if ((e as any).originalTarget) return ((e as any).originalTarget as HTMLElement).closest('a[href]');
            else return (e.target as HTMLElement).closest('a[href]');
        }
        document.body.addEventListener('click', (e) => {
            const link = findLink(e);
            if (link) this.onLinkClicked(link as HTMLAnchorElement, e);
        })
        window.onpopstate = async (e: PopStateEvent) => {
            if (location.search) {
                await this.mainViewer.onNavigate(new URL(location.search.substring(1)), { previousUrl: this.mainViewer.url });
                if (e.state?.scrollLeft && this.myMemo) {
                    this.myMemo.scrollContainer.scrollLeft = e.state.scrollLeft;
                    this.myMemo.scrollContainer.scrollTop = e.state.scrollTop;
                }
            }
        };
    }

    translateError(errMes: string) {
        if (errMes === 'websocket failure' && localStorage.offlineMode)
            return 'application is currently in offline mode';
        return errMes;
    }

    _initApp() {
        const app = this;
        var defaultSidebarXPos = window.innerWidth - 270;
        //this.wndLog = new LogWindow("messages", {x:defaultSidebarXPos,y:205, xx:260,yy:140});
        this.wndLog = this.wndChat = new ChatWindow("chat", {x:defaultSidebarXPos,y:42, xx:260,yy:160});
        
        this.db.errorOccured.connect((errMes) => this.logError(errMes));
        this.remote.errorOccured.connect((errMes) => this.logError(errMes));

        this.wndLogin = new UserMgmtWindow("user-management", {x:133,y:42, xx:250,yy:200});
        
        window.addEventListener("error", (e) => this.logError(`⚠️ ${e.error} *(${e.filename}:${e.lineno})*`, e.error));
        window.addEventListener("unhandledrejection", (e) => this.logError("🔴 "+this.translateError(e.reason))); // reportError(e.reason));//

        this.wndParkplatz = new ActivityWindow("parkplatz", {x:defaultSidebarXPos,y:350, xx:260,yy:260});
        this.wndParkplatz.hideOnClose = true;
        this.wndAutostart = new ActivityWindow("autostart", {x:defaultSidebarXPos,y:350, xx:260,yy:260});
        this.wndAutostart.hideOnClose = true;
        if (!DomWindowConfig.isMobileMode) {
            this.wndAutostartFull = new ActivityWindow("autostart-full", {x:defaultSidebarXPos,y:350, xx:260,yy:260});
            this.wndAutostartFull.hideOnClose = true;
        }

        this.wndFileList = new FileListWindow("filelist", {x:defaultSidebarXPos,y:190, xx:260,yy:140});
        this.wndCalendar = new CalendarWindow("calendar", {x:defaultSidebarXPos,y:190, xx:260,yy:140});

        //this.myMemo = new Memo(document.getElementById("myMemo")!!, this.selectionManager, undefined, {allowUserNavigate: true, alwaysAllowIconClick: true});
        //this.selectionManager.activeMemo = this.myMemo;
    
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Force Mobile Mode On',clickAppMethod:'setConfigOption',param:'"overrideMobileMode"',param2:'"TRUE"',id:'appaction-0'}));
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Force Mobile Mode Off',clickAppMethod:'setConfigOption',param:'"overrideMobileMode"',param2:'"FALSE"',id:'appaction-1'}));
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Switch Audio On',clickAppMethod:'setConfigOption',param:'"enableAudio"',param2:'"TRUE"',id:'appaction-2'}));
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Switch Audio Off',clickAppMethod:'setConfigOption',param:'"enableAudio"',param2:'""',id:'appaction-3'}));
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Collapse All Tool Windows',clickAppMethod:'collapseAllWindows',id:'appaction-4'}));
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Reset Tool Windows',clickAppMethod:'resetToolWindows',id:'appaction-5'}));
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Register Service Worker',clickAppMethod:'registerServiceWorker',id:'appaction-6'}));
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Unregister Service Worker',clickAppMethod:'unregisterServiceWorker',id:'appaction-7'}));
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Update Service Worker',clickAppMethod:'updateServiceWorker',id:'appaction-8'}));
        //this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:'Fetch All Documents',clickAppMethod:'syncDocuments',param:'{"remote":true}',id:'appaction-9'}));
        //this.selectionManager.addStaticCommand(new Command({toolbar:'hidden',title:'Go To Document',hotkey:'ctrl-p',clickAppMethod:'goToDocumentPalette'}));
        [
            //this.wndLog, 
            this.wndChat, this.wndLogin, this.wndParkplatz, this.wndAutostart, this.wndAutostartFull, this.wndFileList, this.wndCalendar
        ].forEach(wnd => {
            if (wnd) this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:`Show ${wnd.title} Window`,clickAppMethod:'showWindow',param:Expression.objectToString(wnd.id),id:'appaction-showwnd-'+wnd.id}));
        });
        this.selectionManager.addStaticCommand(new Command({toolbar:'global',title:`New Search Window`,clickAppMethod:'navigate',param:'"itemsearchwindow:"',hotkey:'ctrl-alt-f',id:'appaction-10'}));

        for(const pluginConstructor of this.availablePlugins) {
            if (!localStorage['disablePlugin:'+pluginConstructor.name])
                this.activePlugins.push(new pluginConstructor());
        }

        function commandFromJson(json: string, el: HTMLElement): Command {
            var result = JSON.parse(json);
            if (!("title" in result) && el) result["title"] = el.getAttribute('title');
            if (!("toolbar" in result)) result["toolbar"] = 'main';
            if ("param" in result) result["param"] = Expression.objectToString(result.param);
            if ("param2" in result) result["param2"] = Expression.objectToString(result.param2);
            return new Command(result);
        }
        $('[data-toolbar-action]').each((index, el) => {
            const command = commandFromJson(el.getAttribute('data-toolbar-action'), el);
            this.selectionManager.addStaticCommand(command);
            el.addEventListener('click', function (e) {
                app.selectionManager.toolbarClick(command, e);
            });
        });
        $('[data-rightclick-toolbar-action]').each((index, el) => {
            const rightClickCommand = commandFromJson(el.getAttribute('data-rightclick-toolbar-action'), el);
            this.selectionManager.addStaticCommand(rightClickCommand);
            el.addEventListener('contextmenu', function (e) {
                e.preventDefault();
                app.selectionManager.toolbarClick(rightClickCommand, e);
            });
        });
        this._initNavigationHandler();
        document.addEventListener("keydown", (e) => {
            var keyname = e.key;
            if (e.altKey) keyname = "alt-" + keyname;
            if (e.ctrlKey) keyname = "ctrl-" + keyname;
            if (keyname == 'ctrl-k' || keyname == 'ctrl-P') {
                e.preventDefault();
                this.showCommandPalette();
            }
            this.selectionManager.handleKeyEvent(e);
        }, false);
        document.onpaste = async (event) => {
            console.log("onpaste", event, "target:", event.target, "active:", document.activeElement, "items:", event.clipboardData.items, "types:", event.clipboardData.types);
            if (!this.selectionManager.isMemoFocused()) {
                console.log("ignoring onpaste because !isMemoFocused"); return
            }
            var items = event.clipboardData.items;
            
            var fileObj = null;
            var strings = {};
            for (var i = 0; i < items.length; i++) {
                if (items[i].kind === 'file') {
                    fileObj = items[i].getAsFile();
                    this.log(`DTI pasted: ${items[i].kind} | ${items[i].type} | ${fileObj.name} | ${fileObj.lastModified}`);
                } else if (items[i].kind === 'string') {
                    var str = event.clipboardData.getData(items[i].type); //await promisify<string>(items[i].getAsString, items[i])();
                    this.log(`DTI pasted: ${items[i].kind} | ${items[i].type} | \`${str}\``);
                    if (items[i].type == "text/plain" && str.startsWith("[{") && str.endsWith("}]")) {
                        var json = JSON.parse(str);
                        if (json.filter(x => (typeof x.id == "string" && typeof x.x == "number" && typeof x.y == "number"))) {
                            this.selectionManager.activeMemo.handlePaste(json);
                            UiContext.instance.toast("info", `Pasted ${json.length} items from clipboard`, 2000);
                            return;
                        }
                    }
                    strings[items[i].type] = str;
                }
            }
            // upload file if there is a pasted file
            if (fileObj !== null) {
                UiContext.instance.toast("info", `Uploading file ...`, 9999);
                var item = await this.selectionManager.activeMemo.uploadFile(fileObj, Object.assign({
                    x:100, y:100, pastedHtml: strings['text/html'], pastedPlaintext: strings['text/plain'],
                }, this.selectionManager.activeMemo.rubberbandRect));
                this.selectionManager.setSelection(item);
                UiContext.instance.toast("info", `Created a file item from clipboard (${fileObj.type}; ${Object.keys(strings).join(", ")})`, 2000);
            } else if ('text/plain' in strings || 'text/html' in strings) {
                var item = this.selectionManager.activeMemo.newItem(Object.assign({
                    x:100, y:100, text: strings['text/plain'] || strings['text/html'], textAlign: "left", pastedHtml: strings['text/html'],
                }, this.selectionManager.activeMemo.rubberbandRect));
                this.selectionManager.setSelection(item);
                UiContext.instance.toast("info", `Created a text item from clipboard (${Object.keys(strings).join(", ")})`, 2000);
            }
            this.lastPasteStringItems = strings;
        }
        $("#btnUploadButton").click(() => {
            $('#btnUpload').click();
        });
        document.getElementById("btnUpload")!!.onchange = function(event) {
            var fileList = (this as HTMLInputElement).files!!;
            for (var i = 0; i < fileList.length; i++) {
                app.wndLog.print(`File selected: ${fileList[i].name} | ${fileList[i].type} | ${fileList[i].size}`);
                app.selectionManager.activeMemo.uploadFile(fileList[i], Object.assign({
                    x:100, y:100
                }, app.selectionManager.activeMemo.rubberbandRect));
            }
        }
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener("message", (e) => {
                console.log("Service Worker: ",e.data);
                if (e.data.new_worker_activated) {
                    UiContext.instance.toast("update", "New App Version Available.", 0, [["Update Now!", () => (window.location.reload as any)(true)]]);
                } else if (e.data.push_notification) {
                    UiContext.instance.toast("info", e.data.push_notification.title + ": " + e.data.push_notification.message, 0, [["Click!", () => this.notificationClicked.emit(e.data.push_notification)]]);
                } else if (e.data.notification_click) {
                    this.notificationClicked.emit(e.data.push_notification);
                }
            });
            navigator.serviceWorker
            .register("/sw.js", { scope: "/" })
            .then((registration) => {
                // registration worked
                this.log("Service Worker Registration succeeded.");
                this.serviceWorkerRegistration = registration;

                this.serviceWorkerRegistration.pushManager.subscribe({
                    userVisibleOnly: true
                }).then((pushSubscription) => {
                    console.log("pushSubscription", pushSubscription)
                    this.log("Push registration succeeded: "+pushSubscription.endpoint);
                    // The push subscription details needed by the application
                    // server are now available, and can be sent to it using,
                    // for example, the fetch() API.
                },
                (error) => {
                    this.logError("Push registration failed: "+error);
                });
            })
            .catch((error) => {
                this.logError(`Service Worker Registration failed with ${error}`);
            });
        } else {
            this.logError(`warning: no service worker support`);
        }

        const pb = ProgressBar.show('loading editor');
        import('./cm/EditorWindow.js').then(editorWindow => {
            PinboardObjectApi.ui.CodeMirrorWindow = editorWindow.CodeMirrorWindow;
            this.wndEdit = new editorWindow.EditorWindow("editor", {x:550,y:190, xx:260,yy:140});
            this.wndEdit.saveRecoveryState.connect((state) => {
                if (state)
                    localStorage['editorWindowRecoveryState'] = JSON.stringify(state);
                else
                    delete localStorage['editorWindowRecoveryState'];
            })
            this.wndEdit.$wnd.hide();
            if (localStorage['editorWindowRecoveryState']) {
                const onLoaded = () => {
                    this.wndEdit.restoreRecoveryState(JSON.parse(localStorage['editorWindowRecoveryState']));
                    this.wndEdit.show();
                };
                if (Object.keys(this.myMemo?.items || {}).length != 0)
                    onLoaded();
                else
                    this.mainViewer.navigated.once(onLoaded);
            }
            pb[Symbol.dispose]();
        });
        this.markdown.loadPlugins();
        document.getElementById('bookmark_menu').addEventListener('mouseenter', this._updateBookmarkMenu);
        dragdrop1d(document.getElementById('toolbar'), 'x', [{
            button: 1, condition: (o, s) => true, init: (v) => 0, transform: (newval, d) => void(0), finishMove: (newval, d) => {
                if (newval < -20) {
                    DomWindow.initMobileHelper(); DomWindow.showMobileHelper();
                } else if (newval > 20) {
                    DomWindow.showWindowSwitcher();
                }
            }, allowDefault: false,
        }])
    }
    async newSearchWindow(params) {
        params = Object.assign({ id: null, bounds: {x:100,y:100,xx:500,yy:400 } }, params);
        const query = new URLSearchParams();
        if (params.searchTerm) query.set("q", params.searchTerm);
        if (params.cols) query.set("cols", JSON.stringify(params.cols));
        await DomWindow.navigate('itemsearchwindow:?' + query.toString(), { focus: true, id: params?.id, defaultBounds: params?.bounds ?? {x:100,y:100,xx:500,yy:400 } });
    }
    _isAltClick(event?: Event): boolean {
        return event instanceof MouseEvent && event.altKey;
    }
    async navHome(param?, param2?, event?: Event) {
        return await this.navigate($("#um_username").val()+"/welcome", undefined, this._isAltClick(event));
    }
    async navToday(offset: number|string = 0, param2?, event?: Event) {
        const docDate = Date.parseDocDate(this.selectionManager.activeMemo?.documentId);
        return await this.navigate((docDate?.user ?? $("#um_username").val())+"/journal/"+Date.parseRelDatetime(offset, docDate).toLocaleDateString('sv-SV'), undefined, this._isAltClick(event));
    }
    findMemoWithDocument(doc: string, requireEditable = true): Memo {
        return this.memos.find(x => x.documentId == doc && (!x.readonly || !requireEditable));
    }
    async navigate(doc: string, itemOrOptions?: string | NavigationOptions, openPopup: boolean = false, keepScrollPos: boolean | null = null): Promise<ActivityHolder> {
        console.log("NAVIGATE:",doc,itemOrOptions,openPopup);
        if (!doc.includes(':')) {
            doc = 'memo:' + doc;
        }
        if (typeof itemOrOptions === 'string') doc += '#' + itemOrOptions;

        const url = new URL(doc);
        this.storeMru('domWindowMru', url, 15, false, MruList.urlEqHash);
        if (url.protocol === 'memo:') this.storeMru('documentMru', url.pathname, 15);
        return await DomWindow.navigate(url, Object.assign({
            focus: true,
            preferredTarget:
                openPopup ? null :
                (this.selectionManager.activeMemo === this.myMemo && url.protocol === 'memo:') ? this.mainViewer :
                DomWindow.current instanceof ActivityWindow ? DomWindow.current.activityHolder :
                null,
            keepScrollPos
        }, typeof itemOrOptions === 'object' ? itemOrOptions : undefined));
    }
    async showCommandPalette(searchOptions?) {
        var response = SearchableMenu.show([
            Object.assign(this.getCommandPaletteMenuSource(), { label: 'command palette' }),
            Object.assign(DomWindow.getWindowSwitcherMenuSource(null, null), { label: 'open windows'}),
        ], searchOptions);
        if (searchOptions && searchOptions.forResponse) return response;
    }
    async goToDocumentPalette(searchOptions?, navigationOptions?: NavigationOptions) {
        const menu = SearchableMenu.create([
            this.getDocumentMenuSource(() => menu.renderMenu()),
            this.getItemSearchMenuSource(),
            this.getWindowMruMenuSource(),
            this.getBookmarkMenuSource(),
        ], Object.assign({
            mruKey: 'docPalette',
            closeOnClick: true,
            action: (e, item) => {
                this.navigate(item.document || item.text, navigationOptions, e.altKey);
            }
        }, searchOptions));
        return menu;
    }
    async goToDocumentPaletteMainMemo() {
        await this.goToDocumentPalette(undefined, { preferredTarget: this.mainViewer });
    }
    getCommandPaletteMenuSource(): MenuSource {
        return {
            static: async () => {
                var toolbarItems = await this.selectionManager.getToolbarItems();
                var menuItems = [];
                var idSet = new Set();
                var mru = this.readMru('commandPaletteMru');
                const toolbarSpecificityOrder = {'newrect':0, 'multi':0, 'itemctx':1000, 'single':1000, 'hidden':1000, 'memo':10000, 'global':10000};
                for (var toolbarName in toolbarItems) {
                    let hiddenPenalty = 0;
                    if (!this.selectionManager.isToolbarEnabled(toolbarName, undefined, true)) hiddenPenalty = 100000;
                    for(const item of toolbarItems[toolbarName]) {
                        if (!item.title) continue;
                        if (!item.id || idSet.has(item.id)) continue;
                        idSet.add(item.id);
                        let mruHash = `${item.title}+${item.icon}`, mruIndex = mru.indexOf(mruHash);
                        menuItems.push(shallowClone(item.props, {
                            text: `${item.title}`,
                            order: (mruIndex == -1 ? 999 : mruIndex) + hiddenPenalty + toolbarSpecificityOrder[toolbarName],
                            [SearchableMenu.EXTRA_CLASSES]: hiddenPenalty ? ['unlikely'] : [],
                            action: (e) => {
                                this.storeMru('commandPaletteMru', mruHash, 20);
                                this.selectionManager.toolbarClick(item, e);
                            }
                        }));
                    }
                }
                menuItems.sort((a, b) => (a.order - b.order) * 10 + (a.text.localeCompare(b.text)));
                return menuItems;
            }
        }
    }
    prepareDocumentMenuItems(files, specials) {
        const menuItems = [...files].map(document => {
            const specialIndizes = specials.map(s => s[0].indexOf(document));
            const match = specialIndizes.findIndex(s => s !== -1);
            const icon = match === -1 ? null : specials[match][1];
            return {
                text: document,
                order: sum(specialIndizes.map((s, i) => s === -1 ? (specials.length - i) * 1000 : s)),
                icon,
                document,
            };
        });
        menuItems.sort((a, b) => (a.order - b.order) * 10 + (a.text.localeCompare(b.text)));
        return menuItems;
    }
    getWindowMruMenuSource(): MenuSource {
        const mru = this.readMru('domWindowMru') as string[];
        const bookmarks = (this.readMru('documentBookmarksMru') as string[]).filter(x => x.includes(":"));
        const wtypes = DomWindow.windowTypes.flatCollect().flatMap(type => ([`${type.name}:`, `${type.protocol || type.name}:`]));
        return {
            label: 'recent windows', prefix:': ', static: this.prepareDocumentMenuItems(new Set([...mru, ...bookmarks, ...wtypes]), [[mru, '3YyOf7SBXMyHTIXw0SSs1Lq7HIpSymsgK5NkFXYNvoS5GqSSig6uUKKAVqOcU5UjI-JfZTnqn0iDx5MPQk6c8Q'], [bookmarks, 'bookmark_red.png']]),
            allowFreeText: {icon: 'plus_button.png',},
        };
    }
    getBookmarkMenuSource(): MenuSource {
        const bookmarks = this.readMru('documentBookmarksMru') as string[];
        return {
            label: 'bookmarks', prefix:'!b ', static: this.prepareDocumentMenuItems(new Set([...bookmarks]), [[bookmarks, 'bookmark_red.png']]),
        };
    }
    getDocumentMenuSource(updateCallback: Function): MenuSource {
        const favs = (this.readMru('documentBookmarksMru') as string[]).filter(x => !x.includes(":"));
        const mru = this.readMru('documentMru') as string[];
        const files = new Set([
            ...favs,
            ...mru,
        ]);
        const specials = [[mru, 'clock_history_frame.png'], [favs, 'bookmark_red.png']];
        var menuItems = this.prepareDocumentMenuItems(files, specials);

        const addToList = (result: Iterable<string>) => {
            for(const file of result) files.add(file);
            menuItems = this.prepareDocumentMenuItems(files, specials);
            updateCallback();
        }
        this.db.getDocumentIds().then(addToList);
        this.getFilelistRoots().then(roots => {
            for(const mountOptions of roots) {
                const root = mountOptions.user;
                this.remote.rpcMethodCall('pb', 'pb', 'pb', "list_dir", [root], undefined, 2000).then(response => {
                    addToList(response.files as string[]);
                }, error => {
                    this.logError("remote list_dir failed, using local only: " + error);
                });
            }
        });
        return {
            label: 'documents', placeholder: 'find document ...', fn: async (search) => {
                return menuItems.filter((item) => SearchableMenu.filterMenu(item.text, search.toLowerCase())).concat([{
                    text: `Create Document "${search}"`,
                    order: null,
                    icon: 'plus_button.png',
                    document: search,
                }]);
            },
        };
    }
    getItemSearchMenuSource(): MenuSource {
        return {
            label: 'items', prefix: '+', placeholder: 'find memo item ...', fn: async (search) => {
                const matcher = new MemoItemSearchMatcher(search);
                if (!matcher.searchStrings.length) return [];
                let results = await matcher.performSearch(200);
                const ord = (x) => (x.lmt || x.mt);
                results.sort((a, b) => ord(b) - ord(a));
                for (const item of results) {
                    (item as any).action = async (e) => {
                        const holder = await this.navigate(item.document as string, item.id as string) as ActivityHolder;
                        const memo = holder.activity as MemoActivity;
                        if (e.ctrlKey) memo.items[item.id].editText();
                        //else if (e.shiftKey) menu.$search.focus();
                    };
                    item.text = `${item.text}`.substring(0, 200);
                }
                return results;
            },
        };
    }
    setConfigOption(name, value) {
        localStorage[name] = value;
    }
    resetToolWindows() {
        DomWindowConfig.resetWindowBounds();
    }

    bookmarkDocument(mode: string='add', docId: string|null=null) {
        if (!docId) docId = this.selectionManager.activeMemo.documentId;
        if (mode == 'toggle') mode = this.readMru('documentBookmarksMru').indexOf(docId) === -1 ? 'add' : 'remove';
        this.storeMru('documentBookmarksMru', docId, 20, mode == 'remove');
    }
    @BoundMethod()
    _updateBookmarkMenu() {
        document.querySelector('#bookmark_menu ul').replaceChildren(...
            (this.readMru('documentBookmarksMru') as string[]).map(docId => EL('li', {}, EL('a', {'href':'?'+docId}, docId))))
    }
    registerServiceWorker() {
        navigator.serviceWorker.register('/sw.js').then((reg) => {
            this.serviceWorkerRegistration = reg;
        });
    }
    updateServiceWorker() {
        this.serviceWorkerRegistration.update();
    }
    async unregisterServiceWorker() {
        var regs = await navigator.serviceWorker.getRegistrations();
        console.log(regs);
        regs.forEach(reg => reg.unregister());
    }
    modifyBodyClass(action:string,className:string) {
        $("body").toggleClass(className, action=='true' ? true : action=='false' ? false : undefined );
    }
    showWindow(id:string) {
        var w = DomWindow.windows[id];
        if (w.$wnd.is(":focus")) {
            w.hide();
        } else {
            w.show();
        }
    }
    collapseAllWindows() {
        for(const w of Object.values(DomWindow.windows)) {
            w.collapse();
        }
    }

    search(checkFor:string, restrictDocument?: string, cancel?: AbortSignal): Promise<any[]> {
        const matcher = new MemoItemSearchMatcher(checkFor);
        if (restrictDocument) matcher.limitToDocument = restrictDocument;
        return matcher.performSearch(undefined, undefined, cancel);
    }
    setClipboardText(text:string) {
        navigator.clipboard.writeText(`${text}`);
    }

    syncUnsavedChanges() {
        return showLegacySyncDialog();
    }
}
